import { useEffect, type ReactNode } from 'react';
import { Button } from './Button';
import { LockNotice } from './LockNotice';

interface DrawerProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  width?: number;
  /**
   * Why this record cannot be changed by the person looking at it (role or
   * state). When set, the reason is shown at the top, every control in the
   * body is disabled, and the footer becomes a single Close — unless
   * `keepFooterWhenLocked` is set because the footer carries its own
   * state actions (a job card's Reopen).
   */
  locked?: string | null;
  keepFooterWhenLocked?: boolean;
}

/** Side panel for create/edit. Escape closes; the backdrop closes; the
 *  form itself never navigates away, so a half-typed record is not lost
 *  to a mis-click on the sidebar. */
export function Drawer({
  open, title, subtitle, onClose, footer, children, width = 520, locked, keepFooterWhenLocked,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const shownFooter = locked && !keepFooterWhenLocked
    ? (
      <>
        <span className="spacer" />
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </>
    )
    : footer;

  return (
    <div className="drawer__backdrop" onMouseDown={onClose}>
      <aside
        className="drawer"
        style={{ width }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="drawer__header">
          <div>
            <h2 className="drawer__title">{title}</h2>
            {subtitle && <div className="small muted">{subtitle}</div>}
          </div>
          <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="drawer__body">
          {locked && <LockNotice>{locked}</LockNotice>}
          {locked
            ? <fieldset className="fieldset-plain" disabled>{children}</fieldset>
            : children}
        </div>
        {shownFooter && <footer className="drawer__footer">{shownFooter}</footer>}
      </aside>
    </div>
  );
}
